import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine
} from 'recharts';
import { Modal } from 'antd';
import { ExpandOutlined } from '@ant-design/icons';
import { useLoading } from '../../contexts/LoadingContext';
import { getClientType } from '../../services/clientConfig';
import { fetchClientText } from '../../services/clientDataService';
import { createAverageLabel } from '../../components/charts/AverageReferenceLabel';
import './PredictChart.css';


function PredictChart({ selectedStreet }) {
  const selectedDate = 'today'; // Fixed default
  const selectedTimeRange = '15min'; // Fixed default
  const [historicalData, setHistoricalData] = useState([]);
  const [realTimeData, setRealTimeData] = useState([]);
  // availableDates removed as it's no longer used for selection
  const { showLoader, hideLoader } = useLoading();
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const streetId = selectedStreet?.hash || selectedStreet?.properties?.UnifiedID;

  // Load CSV data
  useEffect(() => {
    const loadData = async () => {
      setLoadingData(true);
      showLoader("Caricamento dati predizioni...");
      setError(null);

      try {
        const clientType = getClientType();

        const [historicalText, realTimeText] = await Promise.all([
          fetchClientText(clientType, 'predict/historical_prediction.csv').catch(() => null),
          fetchClientText(clientType, 'predict/fake_real_time.csv').catch(() => null)
        ]);

        if (historicalText) {
          const parsedHistorical = parseCSV(historicalText);
          setHistoricalData(parsedHistorical);
          // availableDates logic removed
        }

        if (realTimeText) {
          setRealTimeData(parseCSV(realTimeText));
        }

        setLoadingData(false);
        hideLoader();
      } catch (err) {
        console.error('Errore nel caricamento dei dati di previsione:', err);
        setError('Caricamento dei dati di previsione fallito');
        setLoadingData(false);
        hideLoader();
      }
    };

    loadData();
  }, []);

  const parseCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.trim());
    return lines.slice(1).map(line => {
      const values = line.split(',');
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index]?.trim() || '';
      });
      return row;
    });
  };

  const getCurrentTime = () => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:00`;
  };

  // Filter and process data for chart
  const chartData = useMemo(() => {
    if (!streetId) return [];

    let dataSource = selectedDate === 'today' ? realTimeData : historicalData;

    let filteredData = dataSource.filter(row =>
      String(row.UnifiedID) === String(streetId) || String(row.UnifiedID) === String(selectedStreet?.properties?.UnifiedID)
    );

    if (selectedDate !== 'today' && selectedDate) {
      filteredData = filteredData.filter(row => row.TestDay === selectedDate);
    }

    const currentTime = getCurrentTime();
    const currentHour = parseInt(currentTime.split(':')[0]);
    const currentMinute = parseInt(currentTime.split(':')[1]);
    const predictionHorizon = 1; // 1 hour ahead

    const processedData = filteredData.map((row) => {
      const timeStr = row.time || (row.timestamp ? row.timestamp.split(' ')[1] : '00:00:00');
      const hour = parseInt(timeStr.split(':')[0]);
      const minute = parseInt(timeStr.split(':')[1]);

      let isPrediction = false;
      if (selectedDate === 'today') {
        const rowMinutes = hour * 60 + minute;
        const currentMinutes = currentHour * 60 + currentMinute;
        isPrediction = rowMinutes > currentMinutes;
        const maxMinutes = currentMinutes + (predictionHorizon * 60);
        if (rowMinutes > maxMinutes) return null;
      }

      const yReal = parseFloat(row.y_real) || null;
      
      // Robustly find the prediction column matching the selected time range
      const timeNum = selectedTimeRange.replace(/\D/g, ''); // e.g. "15"
      let rawPrediction = row[selectedTimeRange];
      
      // If exact match fails, find a column containing the number (e.g. "15 min", "15_min")
      if (rawPrediction === undefined) {
        const possibleKey = Object.keys(row).find(k => k.includes(timeNum) && k !== 'UnifiedID' && k !== 'time' && k !== 'timestamp');
        if (possibleKey) {
          rawPrediction = row[possibleKey];
        }
      }

      const predictionValue = rawPrediction !== '' && rawPrediction != null ? parseFloat(rawPrediction) : null;
      const validPrediction = !isNaN(predictionValue) ? predictionValue : null;

      // Historical dates: show ground truth + prediction for comparison
      if (selectedDate !== 'today') {
        return {
          time: timeStr.substring(0, 5),
          groundTruth: yReal,
          prediction: validPrediction,
          isPrediction: false,
          hour,
          minute
        };
      }

      // Today: ground truth up to now, prediction for future 1h
      return {
        time: timeStr.substring(0, 5),
        groundTruth: isPrediction ? null : yReal,
        prediction: isPrediction ? validPrediction : null,
        isPrediction,
        hour,
        minute
      };
    }).filter(Boolean).sort((a, b) => (a.hour * 60 + a.minute) - (b.hour * 60 + b.minute));

    // Fix the visual gap between ground truth and prediction by overlapping the transition point
    if (selectedDate === 'today') {
      const firstPredictionIdx = processedData.findIndex(d => d.isPrediction);
      if (firstPredictionIdx > 0) {
        const lastGroundTruth = processedData[firstPredictionIdx - 1];
        lastGroundTruth.prediction = lastGroundTruth.groundTruth;
      }
    }

    return processedData;
  }, [historicalData, realTimeData, streetId, selectedStreet]);

  const predictionStartTime = useMemo(() => {
    const firstPredictionIdx = chartData.findIndex(d => d.isPrediction);
    if (firstPredictionIdx > 0) {
      // Return the time of the point BEFORE the first prediction to cover the transition segment
      return chartData[firstPredictionIdx - 1].time;
    }
    return chartData[firstPredictionIdx]?.time || null;
  }, [chartData]);

  // Check if there's any prediction data for the selected time range
  const averageValue = useMemo(() => {
    if (chartData.length === 0) return null;
    const values = chartData.map(d => d.groundTruth).filter(v => v !== null);
    if (values.length === 0) return null;
    return values.reduce((a, b) => a + b, 0) / values.length;
  }, [chartData]);

  const hasAnyPrediction = useMemo(() => {
    return chartData.some(d => d.prediction != null);
  }, [chartData]);

  const hasAnyData = chartData.length > 0;

  const CustomTooltip = useCallback(({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="predict-chart-tooltip">
          <p className="tooltip-time">{label}</p>
          {payload.map((entry, index) => (
            entry.value != null && (
              <p key={index} style={{ color: entry.color }}>
                {entry.name}: {entry.value?.toFixed(2)}
              </p>
            )
          ))}
          {data.isPrediction && (
            <p className="tooltip-predicted">Valore predetto</p>
          )}
        </div>
      );
    }
    return null;
  }, []);

  const renderChart = (height) => {
    if (!hasAnyData) {
      return (
        <div className="predict-chart-no-data" style={{ height }}>
          <p>Nessun dato disponibile per questa strada</p>
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={chartData}
          margin={{ top: 28, right: 20, left: 10, bottom: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis dataKey="time" tick={{ fontSize: 11 }} stroke="#666" />
          <YAxis
            tick={{ fontSize: 11 }}
            stroke="#666"
            label={{ value: 'Valore', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />

          {predictionStartTime && (
            <ReferenceArea
              x1={predictionStartTime}
              x2={chartData[chartData.length - 1]?.time}
              fill="#3B82F6"
              fillOpacity={0.1}
              stroke="#3B82F6"
              strokeOpacity={0.2}
            />
          )}

          {predictionStartTime && (
            <ReferenceLine
              x={predictionStartTime}
              stroke="#666"
              strokeDasharray="3 3"
              strokeWidth={1}
            />
          )}

          <Line
            type="monotone"
            dataKey="groundTruth"
            stroke="#3B82F6"
            strokeWidth={3}
            dot={false}
            name="Valore Reale"
            connectNulls={false}
          />

          <Line
            type="monotone"
            dataKey="prediction"
            stroke="#F59E0B"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
            name="Previsione"
            connectNulls={false}
          />

          {averageValue !== null && (
            <ReferenceLine
              y={averageValue}
              stroke="#999999"
              strokeDasharray="3 3"
              strokeWidth={2}
              label={createAverageLabel({
                text: `Media: ${averageValue.toFixed(1)}`,
                fill: '#999999',
                fontSize: 10,
              })}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  if (loadingData) {
    return (
      <div className="predict-chart-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '250px' }}>
        <div className="predict-chart-no-data">
          <p>Elaborazione dati in corso...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="predict-chart-error">
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="predict-chart-wrapper">
      {/* Prediction reference date — always visible for temporal context */}
      <div className="predict-chart-date-banner" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '6px 12px',
        marginBottom: '8px',
        background: 'rgba(59, 130, 246, 0.08)',
        borderRadius: '6px',
        fontSize: '12px',
        color: '#334155',
      }}>
        <span>
          Data di riferimento:{' '}
          <strong>
            {new Date().toLocaleDateString('it-IT', {
              weekday: 'long',
              day: '2-digit',
              month: 'long',
              year: 'numeric',
            })}
          </strong>
          {' '}(oggi)
        </span>
        <span style={{ color: '#64748b' }}>Orizzonte: 15 min</span>
      </div>

      {/* Chart */}
      <div className="predict-chart-area">
        {chartData.length > 0 && (
          <button
            onClick={() => setIsExpanded(true)}
            className="predict-expand-btn"
            title="Espandi grafico"
          >
            <ExpandOutlined />
          </button>
        )}
        {renderChart(250)}
        {hasAnyData && !hasAnyPrediction && (
          <div className="predict-chart-no-prediction-msg">
            Nessun dato di previsione disponibile per l'orizzonte selezionato
          </div>
        )}
      </div>

      {selectedDate === 'today' && (
        <div className="predict-chart-info">
          <span className="predict-info-text">
            L'area evidenziata mostra la previsione per la prossima ora
          </span>
        </div>
      )}

      {/* Expanded Modal */}
      <Modal
        open={isExpanded}
        onCancel={() => setIsExpanded(false)}
        footer={null}
        width="95%"
        centered
        title={`Previsione — ${selectedStreet?.properties?.StreetName || streetId}`}
        destroyOnClose
        zIndex={9999}
        style={{ top: 20 }}
      >
        {/* Modal controls removed */}
        <div style={{ padding: '20px 0' }}>
          {renderChart(500)}
        </div>
      </Modal>
    </div>
  );
}

export default PredictChart;
